/**
 * Table component for canvas rendering.
 * Displays data in a table with pagination and optional virtualization.
 * Uses react-window v2 API for virtualization.
 */

import React, { memo, useState, useMemo } from 'react';
import { List } from 'react-window';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { TableProps as TablePropsType, TableColumn } from '../../../types/canvas';

export interface TableComponentProps extends TablePropsType {}

const VIRTUALIZATION_THRESHOLD = 100;
const ROW_HEIGHT = 44;

// Props passed via rowProps (custom data)
interface VirtualRowData {
  rows: Record<string, unknown>[];
  columns: TableColumn[];
}

// Full props received by the row component (List provides index and style)
interface VirtualRowProps extends VirtualRowData {
  index: number;
  style: React.CSSProperties;
}

function VirtualRow({ index, style, rows, columns }: VirtualRowProps) {
  const row = rows[index];

  return (
    <div
      style={style}
      className={`flex items-center border-b border-gray-100 dark:border-gray-800 ${index % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50 dark:bg-gray-800/50'}`}
    >
      {columns.map((col) => (
        <div
          key={col.key}
          className={`px-4 py-2 text-sm text-gray-700 dark:text-gray-200 truncate ${
            col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'
          }`}
          style={{ width: col.width || `${100 / columns.length}%`, flexShrink: 0 }}
        >
          {String(row[col.key] ?? '')}
        </div>
      ))}
    </div>
  );
}

function TableComponentBase({ columns, rows, pageSize = 10 }: TableComponentProps) {
  const [currentPage, setCurrentPage] = useState(0);

  const shouldVirtualize = rows.length > VIRTUALIZATION_THRESHOLD;

  const totalPages = Math.ceil(rows.length / pageSize);
  const paginatedRows = useMemo(() => {
    if (shouldVirtualize) return rows;
    const start = currentPage * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, currentPage, pageSize, shouldVirtualize]);

  const goToPage = (page: number) => {
    setCurrentPage(Math.max(0, Math.min(page, totalPages - 1)));
  };

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="flex bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
        {columns.map((col) => (
          <div
            key={col.key}
            className={`px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider ${
              col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'
            }`}
            style={{ width: col.width || `${100 / columns.length}%`, flexShrink: 0 }}
          >
            {col.header}
          </div>
        ))}
      </div>

      {/* Body */}
      {shouldVirtualize ? (
        <List<VirtualRowData>
          rowComponent={VirtualRow}
          rowCount={rows.length}
          rowHeight={ROW_HEIGHT}
          rowProps={{ rows, columns }}
          style={{ height: Math.min(400, rows.length * ROW_HEIGHT), width: '100%' }}
        />
      ) : (
        <div>
          {paginatedRows.map((row, idx) => (
            <div
              key={idx}
              className={`flex items-center border-b border-gray-100 dark:border-gray-800 last:border-b-0 ${
                idx % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50 dark:bg-gray-800/50'
              }`}
            >
              {columns.map((col) => (
                <div
                  key={col.key}
                  className={`px-4 py-3 text-sm text-gray-700 dark:text-gray-200 truncate ${
                    col.align === 'right'
                      ? 'text-right'
                      : col.align === 'center'
                        ? 'text-center'
                        : 'text-left'
                  }`}
                  style={{ width: col.width || `${100 / columns.length}%`, flexShrink: 0 }}
                >
                  {String(row[col.key] ?? '')}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Pagination (only for non-virtualized tables) */}
      {!shouldVirtualize && totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Showing {currentPage * pageSize + 1} to{' '}
            {Math.min((currentPage + 1) * pageSize, rows.length)} of {rows.length} rows
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 0}
              className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm text-gray-700 dark:text-gray-200">
              Page {currentPage + 1} of {totalPages}
            </span>
            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage >= totalPages - 1}
              className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Row count for virtualized tables */}
      {shouldVirtualize && (
        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 text-sm text-gray-500 dark:text-gray-400">
          {rows.length} rows (virtualized)
        </div>
      )}
    </div>
  );
}

export const TableComponent = memo(TableComponentBase);
